import { PropsWithChildren, useEffect } from 'react';

import { useFormState } from 'react-final-form';

import { PreparedWorkload } from 'features/workload-optimization';

import { useShowHpaV2 } from '../../_hooks/permissions/useShowHpaV2';
import { useWorkloadHasLegacyHpaSettings } from '../../_hooks/useWorkloadHasLegacyHpaSettings';
import { RightsizingSettingsForm } from './RightsizingSettingsForm';
import { RightsizingSettingsFormV2 } from './RightsizingSettingsFormV2';
import { RightsizingSettingsRouteGuard } from './RightsizingSettingsRouteGuard';

const FormStateSync = ({
  trackHpaSettingsMissing,
  onDirtyChange,
  onHpaSettingsMissingChange,
}: {
  trackHpaSettingsMissing: boolean;
  onDirtyChange?: (dirty: boolean) => void;
  onHpaSettingsMissingChange?: (missing: boolean) => void;
}) => {
  const { dirty, values } = useFormState({
    subscription: trackHpaSettingsMissing
      ? { dirty: true, values: true }
      : { dirty: true },
  });

  useEffect(() => {
    onDirtyChange?.(dirty);
    if (trackHpaSettingsMissing) {
      onHpaSettingsMissingChange?.(!values?.horizontalRightsizing?.hpaSettings);
    }
  }, [
    dirty,
    trackHpaSettingsMissing,
    values,
    onDirtyChange,
    onHpaSettingsMissingChange,
  ]);

  return null;
};

type Props = PropsWithChildren<{
  workload: PreparedWorkload;
  /** When true, blocks route leave when form is dirty (except allowed suffixes). */
  enableRouteLeaveGuard?: boolean;
  allowedRouteSuffixes?: string[];
  onDirtyChange?: (dirty: boolean) => void;
  onHpaSettingsMissingChange?: (missing: boolean) => void;
}>;

/**
 * Chooses RightsizingSettingsForm (V1 / legacy) vs FormV2 and wraps children.
 * Shared by classic WOOP RightsizingDetails and inventory workload settings.
 */
export const RightsizingSettingsShell = ({
  workload,
  children,
  enableRouteLeaveGuard = false,
  allowedRouteSuffixes = [],
  onDirtyChange,
  onHpaSettingsMissingChange,
}: Props) => {
  const { showHpaV2 } = useShowHpaV2();
  const { workloadHasLegacyHpaSettings } = useWorkloadHasLegacyHpaSettings({
    workload,
  });

  const useV2Form = showHpaV2 && !workloadHasLegacyHpaSettings;
  const Form = useV2Form ? RightsizingSettingsFormV2 : RightsizingSettingsForm;

  return (
    <Form workload={workload}>
      {enableRouteLeaveGuard && (
        <RightsizingSettingsRouteGuard
          allowedPathSuffixes={allowedRouteSuffixes}
        />
      )}
      <FormStateSync
        trackHpaSettingsMissing={useV2Form}
        onDirtyChange={onDirtyChange}
        onHpaSettingsMissingChange={onHpaSettingsMissingChange}
      />
      {children}
    </Form>
  );
};
